/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	ClaimConstants
} from '@claims/constants/claims-constants';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
/* eslint-enable max-len */

@Component({
	selector: 'add-claim-recovery',
	templateUrl: './add-claim-recovery.component.html',
	styleUrls: []
})

/**
 * A component representing a wizard step for adding claim recovery.
 *
 * @export
 * @class AddClaimRecoveryComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class AddClaimRecoveryComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the add claim recovery component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes on entity creation.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @memberof AddClaimRecoveryComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public entityInstanceApiService: EntityInstanceApiService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof AddClaimRecoveryComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof AddClaimRecoveryComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the recovery details markdown.
	 *
	 * @type {Promise<string>}
	 * @memberof AddClaimRecoveryComponent
	 */
	private recoveryDetails: Promise<string>;

	/**
	 * Gets or sets the claim id.
	 *
	 * @type {number}
	 * @memberof AddClaimRecoveryComponent
	 */
	private claimId: number;

	/**
	 * Gets or sets the recovery amount.
	 *
	 * @type {number}
	 * @memberof AddClaimRecoveryComponent
	 */
	private recoveryAmount: number;

	/**
	 * Gets or sets the recovery type.
	 *
	 * @type {string}
	 * @memberof AddClaimRecoveryComponent
	 */
	private recoveryType: string;

	/**
	 * Gets or sets the override amount boolean.
	 *
	 * @type {boolean}
	 * @memberof AddClaimRecoveryComponent
	 */
	private overrideAmount: boolean;

	/**
	 * Gets or sets the note.
	 *
	 * @type {string}
	 * @memberof AddClaimRecoveryComponent
	 */
	private note: string;

	/**
	 * Gets or sets the exposure resource identifier.
	 *
	 * @type {string}
	 * @memberof AddClaimRecoveryComponent
	 */
	private exposureResourceIdentifier: string;

	/**
	 * Gets or sets the expense type.
	 *
	 * @type {string}
	 * @memberof AddClaimRecoveryComponent
	 */
	private expenseType: string;

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof AddClaimRecoveryComponent
	 */
	private currentData: any;

	/**
	 * Implements the on initialization interface.
	 *
	 * @async
	 * @memberof AddClaimRecoveryComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.currentData =
			this.context.source.activeMenuItem.currentData;

		this.claimId = this.currentData.data.id;

		this.exposureResourceIdentifier =
			this.currentData.data.exposure.identifier;

		this.expenseType =
			this.currentData.data.recovery.expenseType;

		this.recoveryAmount =
			this.currentData.data.recovery.amount;

		this.recoveryType =
			this.currentData.data.recovery.recoveryType;
			
		this.overrideAmount =
			this.currentData.data.recovery.overrideAmount;

		this.note = 
			this.currentData.data.recovery.note 
				?? AppConstants.empty;

		this.recoveryDetails = this.getRecoveryDetails();

		this.context.source.addToNext(this.create.bind(this));

		await this.performPostInitActions();

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @async
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof AddClaimRecoveryComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		this.context.source.validStepChanged(isValid);
	}

	/**
	 * This will send the claim recovery create event and navigate to the new
	 * entity.
	 *
	 * @async
	 * @memberof AddClaimRecoveryComponent
	 */
	public async create(): Promise<void>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			ClaimConstants.claimEntityTypeGroups.claims;

		const parameterString: string =
				 '&exposureResourceIdentifier='
				 	+ `${this.exposureResourceIdentifier}`
					+ '&expenseType='
					+ `${this.expenseType}`
					+ '&recoveryAmount='
					+ `${this.recoveryAmount}`
					+ '&recoveryType='
					+ `${this.recoveryType}`
					+ '&overrideAmount='
					+ `${this.overrideAmount}`
					+ '&note='
					+ `${this.note}`;

		await this.activityService.handleActivity(
			new Activity(
				this.entityInstanceApiService.executeAction(
					this.claimId,
					ClaimConstants.workflowActions.addRecovery,
					null,
					parameterString),
				'<strong>Adding Recovery</strong>',
				'<strong>Recovery Added</strong>',
				'Recovery has been added.',
				'Recovery has not been added.'));

		await this.navigateToClaimSummary(
			this.claimId,
			ClaimConstants.claimEntityTypeGroups.claims);
	}

	/**
	 * This gets the recovery summary information to display.
	 *
	 * @async
	 * @memberof AddClaimRecoveryComponent
	 */
	public async getRecoveryDetails(): Promise<string>
	{
		const details: string =
			'<strong>Recovery Details</strong><br/>'
				+ `Expense Type: ${this.expenseType}<br/>`
				+ `Recovery Type: ${this.recoveryType}<br/>`
				+ `Amount: $${this.recoveryAmount.toFixed(2)}<br/>`
				+ `Note: ${this.note || 'No note provided'}`;

		return details;
	}

	/**
	 * This will navigate to the claim summary dashboard the claim id
	 * provided.
	 *
	 * @async
	 * @param {number} entityId
	 * The claim entity id to navigate.
	 * @param {string} group
	 * The entity group associated to the navigation.
	 * @memberof AddClaimRecoveryComponent
	 */
	private async navigateToClaimSummary(
		entityId: number,
		group: string)
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				group,
				AppConstants.viewTypes.edit,
				entityId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}

	/**
	 * Handles the post initialization action.
	 * This will create the dynamic formly layout for display component creation
	 * and permissions.
	 *
	 * @async
	 * @memberof AddClaimRecoveryComponent
	 */
	private async performPostInitActions(): Promise<void>
	{
		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				<FormlyFieldConfig>
				{
					type: FormlyConstants.customControls.customTextDisplay,
					props: {
						centerText: true,
						useMarkDown: true,
						usePanelDisplay: false,
						content: await this.recoveryDetails
					}
				}
			];
	}
}